import type { OrganizationMemberRole } from '@/lib/services/organization-api';
import { stripOrgSlug } from '@/lib/org-path';

/**
 * Which college pages each member role can use. Mirrors the backend's checks
 * (OrganizationService role permissions, PLACEMENT_MANAGE for running drives,
 * AcademicStructureService batch scoping) so the sidebar never offers a page
 * the server would refuse. The server remains the authority.
 */
export type CollegeSection =
  | 'dashboard'
  | 'setup'
  | 'students'
  | 'inviteStudents'
  | 'staff'
  | 'structure'
  | 'records'
  | 'insights'
  | 'careers'
  | 'drives'
  | 'manageDrives'
  | 'companies'
  | 'optouts'
  | 'analytics'
  | 'reports'
  | 'documents'
  | 'resumeReviews'
  | 'assessments'
  | 'notices'
  | 'messages'
  | 'settings'
  | 'billing';

type Role = OrganizationMemberRole;

const ADMINS: Role[] = ['OWNER', 'ADMIN'];
/** Everyone who works at the college, as opposed to students. */
export const COLLEGE_STAFF_ROLES: Role[] = [
  'OWNER',
  'ADMIN',
  'HOD',
  'PLACEMENT_OFFICER',
  'INTERNAL_FACULTY',
  'EXTERNAL_FACULTY',
  'STAFF',
];
/** Drive pages: the placement cell runs them; department heads follow them read-only. */
const PLACEMENT: Role[] = ['OWNER', 'ADMIN', 'HOD', 'PLACEMENT_OFFICER'];
/** PLACEMENT_MANAGE holders: create drives, record rounds and offers. */
const PLACEMENT_CELL: Role[] = ['OWNER', 'ADMIN', 'PLACEMENT_OFFICER'];
/** STUDENTS_VIEW holders. */
const STUDENT_VIEWERS: Role[] = ['OWNER', 'ADMIN', 'HOD', 'PLACEMENT_OFFICER', 'INTERNAL_FACULTY'];
/** STUDENTS_MANAGE holders (records uploads). */
const STUDENT_MANAGERS: Role[] = ['OWNER', 'ADMIN', 'HOD', 'PLACEMENT_OFFICER'];

export const COLLEGE_ACCESS: Record<CollegeSection, Role[]> = {
  dashboard: COLLEGE_STAFF_ROLES,
  setup: ADMINS,
  students: STUDENT_VIEWERS,
  // Inviting and roster import need ORG_INVITE_MEMBERS, which only admins hold by default.
  inviteStudents: ADMINS,
  staff: ADMINS,
  structure: COLLEGE_STAFF_ROLES,
  records: STUDENT_MANAGERS,
  insights: COLLEGE_STAFF_ROLES,
  careers: STUDENT_VIEWERS,
  drives: PLACEMENT,
  manageDrives: PLACEMENT_CELL,
  companies: PLACEMENT,
  // Reviewing opt-outs needs PLACEMENT_MANAGE, which HODs don't hold.
  optouts: ['OWNER', 'ADMIN', 'PLACEMENT_OFFICER'],
  analytics: PLACEMENT,
  reports: STUDENT_VIEWERS,
  documents: STUDENT_MANAGERS,
  resumeReviews: STUDENT_VIEWERS,
  assessments: STUDENT_MANAGERS,
  notices: STUDENT_MANAGERS,
  messages: COLLEGE_STAFF_ROLES,
  settings: ADMINS,
  billing: ADMINS,
};

/**
 * With a custom role the server checks the role's own permission list instead of
 * the defaults (OrganizationService.verifyPermission), so these sections follow
 * it too. Sections missing here are decided by the base role alone:
 * batch-scoped views need a staff base role.
 */
const SECTION_PERMISSIONS: Partial<Record<CollegeSection, string[]>> = {
  setup: ['ORG_MANAGE_SETTINGS'],
  settings: ['ORG_MANAGE_SETTINGS'],
  billing: ['ORG_MANAGE_BILLING'],
  staff: ['ORG_MANAGE_ROLES'],
  students: ['STUDENTS_VIEW'],
  inviteStudents: ['ORG_INVITE_MEMBERS'],
  careers: ['STUDENTS_VIEW'],
  reports: ['STUDENTS_VIEW'],
  records: ['STUDENTS_MANAGE'],
  documents: ['STUDENTS_MANAGE'],
  resumeReviews: ['STUDENTS_VIEW'],
  assessments: ['STUDENTS_MANAGE'],
  optouts: ['PLACEMENT_MANAGE'],
  drives: ['PLACEMENT_MANAGE'],
  manageDrives: ['PLACEMENT_MANAGE'],
  notices: ['PLACEMENT_MANAGE', 'STUDENTS_MANAGE'],
};

/** Sections that show every batch when a custom role grants STUDENTS_VIEW. */
const SCOPED_VIEWS: CollegeSection[] = ['dashboard', 'structure', 'insights', 'analytics'];

/**
 * `null` role means it hasn't loaded yet: allow, so the sidebar doesn't flicker
 * for the common admin case (the server still enforces).
 */
export function canUseCollegeSection(
  role: Role | null | undefined,
  section: CollegeSection,
  customPermissions?: string[] | null
): boolean {
  if (!role) return true;
  if (role === 'OWNER' || role === 'ADMIN') return true;
  if (customPermissions) {
    const needed = SECTION_PERMISSIONS[section];
    if (needed) return needed.some((p) => customPermissions.includes(p));
    if (SCOPED_VIEWS.includes(section) && customPermissions.includes('STUDENTS_VIEW')) return true;
  }
  return COLLEGE_ACCESS[section].includes(role);
}

/** Longest-prefix match of a path to a college section, for the page-level gate. */
const SECTION_PATHS: Array<[string, CollegeSection]> = [
  ['/college/dashboard', 'dashboard'],
  ['/college/setup', 'setup'],
  ['/college/students/profiles', 'careers'],
  ['/college/students/resume-reviews', 'resumeReviews'],
  ['/college/students/invite', 'inviteStudents'],
  ['/college/students/import', 'inviteStudents'],
  ['/college/students', 'students'],
  ['/college/staff', 'staff'],
  ['/college/structure', 'structure'],
  ['/college/records', 'records'],
  ['/college/skill-insights', 'insights'],
  ['/college/drives/create', 'manageDrives'],
  ['/college/drives', 'drives'],
  ['/college/companies', 'companies'],
  ['/college/opt-outs', 'optouts'],
  ['/college/analytics', 'analytics'],
  ['/college/assessments/reports', 'reports'],
  ['/college/assessments', 'assessments'],
  ['/college/documents', 'documents'],
  ['/college/notices', 'notices'],
  ['/college/messages', 'messages'],
  ['/college/settings', 'settings'],
  ['/college/billing', 'billing'],
  ['/college/checkout', 'billing'],
];

export function sectionForPath(fullPathname: string): CollegeSection | null {
  const pathname = stripOrgSlug(fullPathname);
  let best: [string, CollegeSection] | null = null;
  for (const entry of SECTION_PATHS) {
    const [prefix] = entry;
    if (
      (pathname === prefix || pathname.startsWith(`${prefix}/`)) &&
      (!best || prefix.length > best[0].length)
    ) {
      best = entry;
    }
  }
  return best?.[1] ?? null;
}
